import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { AuthService } from '../services/auth.service';
import { RegistrationService } from '../services/registration.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { SingleResultApiResponse } from '../models/api-response.model';
import { ErrorResponse, AWSCognitoErrorResponse } from '../models/error.model';
import { CreatedStudentModel } from '../models/created-student.model';

@Component({
  selector: 'online-app-account-registration',
  templateUrl: './account-registration.component.html',
  styleUrls: ['./account-registration.component.scss'],
  host: {
    class:'full-view-port-component-with-no-side-nav'
  }
})
export class AccountRegistrationComponent implements OnInit, OnDestroy {
  constructor(
    private formBuilder: FormBuilder,
    private store: Store<any>,
    private router: Router,
    private authService: AuthService,
    private registrationService: RegistrationService
  ) {}

  accountCreationFormGroup: FormGroup;
  sharedStateSubscription: Subscription;
  createdStudent: CreatedStudentModel;
  accountCreated = false;
  isFormSubmitted = false;
  isAccountAreadyCreated = false;
  isAccountAreadyVerified = false;
  customErrorMsg = '';

  ngOnInit() {

    //console.log('in account registration oninit');
    this.sharedStateSubscription = this.store
    .pipe(select('shared'))
    .subscribe(sharedState => {
      if (sharedState) {
        const preRegistrationDetails = sharedState.preRegistrationDetails;
        const preRegistrationValidationResult = sharedState.preRegistrationValidationResult;

        


        if (preRegistrationValidationResult &&
            preRegistrationValidationResult.IsGdprRequired &&
            !preRegistrationDetails.IsGdprCompliant ) {

          //console.log('redirecting to pre-registration-gdpr');
          this.router.navigate(['pre-registration-gdpr']);
        }

        if (!preRegistrationDetails) {
          //console.log('redirecting to pre-registration');
          this.router.navigate(['pre-registration']);
        }


      if (preRegistrationDetails && preRegistrationDetails.IsGdprCompliant && preRegistrationValidationResult  ) {

        //console.log('going to call create student');

          this.registrationService.createStudent(preRegistrationDetails)
          .subscribe( (response: SingleResultApiResponse) => {
            this.createdStudent = response.Result as CreatedStudentModel;
          }, (errorResponse: ErrorResponse) => {
            console.log(errorResponse);
            
            if (errorResponse.errorCode === 400) {
              if (errorResponse.errors.some(e => e.Key === 'AlreadyRegistered')) {
                this.accountCreated = true;
                this.isAccountAreadyCreated = true;
              }

              if (errorResponse.errors.some(e => e.Key === 'AccountAlreadyVerified')) {
                this.accountCreated = true;
                this.isAccountAreadyCreated = true;
                this.isAccountAreadyVerified = true;
              }
            }

          }, () => {
          });

      }

       

      } else {
        this.router.navigate(['pre-registration']);
      }
    });


    this.accountCreationFormGroup = this.formBuilder.group(
      {
        accountPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required, Validators.minLength(6)]]
      }
    );

  }

  validatePasswords(): { [key: string]: boolean } | null {
    const accountPassword = this.accountCreationFormGroup.get('accountPassword').value;
    const confirmPassword = this.accountCreationFormGroup.get('confirmPassword').value;
    return accountPassword && accountPassword === confirmPassword
      ? null
      : { passwordsDontMatch: true };
  }

  createAccount(): void {

    this.isFormSubmitted = true;
    const accountPassword = this.accountCreationFormGroup.get('accountPassword').value;
    const confirmPassword = this.accountCreationFormGroup.get('confirmPassword').value;

    if (accountPassword !== confirmPassword) {
      this.accountCreationFormGroup.get('accountPassword').setErrors({ passwordsDontMatch: true });
      this.accountCreationFormGroup.updateValueAndValidity();
      return;
    }
    else {  
      this.accountCreationFormGroup.get('accountPassword').setErrors(null);
      this.accountCreationFormGroup.updateValueAndValidity();
    }


    if (this.accountCreationFormGroup.valid) {
      const { StudentNumber, EmailAddress } = this.createdStudent;

      this.authService
        .register(StudentNumber, accountPassword, EmailAddress)
        .subscribe(
          signUpResult => {
            // Show the user some msg asking them to confirm their email
            //console.log('successfully registered with AWS Cognito');
            //console.log(signUpResult);

            this.registrationService.registerStudent(StudentNumber)
                .subscribe( (response: SingleResultApiResponse) => {
                  //console.log(response);
                  this.accountCreated = true;
                }, (err) => {
                  console.log('Error');
                });
          },
          (err: AWSCognitoErrorResponse) => {

            if ( err.code === 'InvalidParameterException') {
              this.accountCreationFormGroup.get('accountPassword').setErrors({ passwordStrength: true });
            } else {
              this.accountCreationFormGroup.get('accountPassword').setErrors({ customError: true });
              this.customErrorMsg = err.message;
            }

            this.accountCreationFormGroup.updateValueAndValidity();

            // console.log(
            //   'error happened while registering the user with AWS Cognito'
            // );
            console.log(err);
          }
        );
    }
  }

  ngOnDestroy() {
    this.sharedStateSubscription.unsubscribe();
  }

  errorPasswordAccessibility(): string{
    if(this.isFormSubmitted &&
      this.accountCreationFormGroup.get('accountPassword').errors &&
      this.accountCreationFormGroup.get('accountPassword').errors.required){
        return "requiredPassword";
    }else if(this.isFormSubmitted &&
      this.accountCreationFormGroup.get('accountPassword').errors &&
      this.accountCreationFormGroup.get('accountPassword').errors.passwordsDontMatch){
        return "matchPassword";
    }else if(this.isFormSubmitted &&
      this.accountCreationFormGroup.get('accountPassword').errors &&
      this.accountCreationFormGroup.get('accountPassword').errors.passwordStrength){
        return "strengthPassword";
    }else if(this.isFormSubmitted &&
      this.accountCreationFormGroup.get('accountPassword').errors &&
      this.accountCreationFormGroup.get('accountPassword').errors.minlength){
        return "minlengthPassword";
    }else if(this.isFormSubmitted &&
      this.accountCreationFormGroup.get('accountPassword').errors &&
      this.accountCreationFormGroup.get('accountPassword').errors.customError){
        return "customErrorPassword";
    }else {
      return "";
    }
  }

  errorConfirmPasswordAccessibility(): string {
    if (this.isFormSubmitted &&
      this.accountCreationFormGroup.get('confirmPassword').errors &&
      this.accountCreationFormGroup.get('confirmPassword').errors.required) {
      return "reEnterConfirmPassword";
    } else if (this.isFormSubmitted &&
      this.accountCreationFormGroup.get('confirmPassword').errors &&
      this.accountCreationFormGroup.get('confirmPassword').errors.minlength) {
      return "minlengthConfirmPassword";
    } else {
      return "";
    }
  }

  //show border when tab on link for accessibility
  linkTabAccessibility(event): void {
    event.style.outline = "1px solid black";
  }

  linkBlurAccessibility(event): void {
    event.style.outline = "";
  }

  // @HostListener('window:popstate', ['$event'])
  // onPopState(event) {
  //   console.log('Back or forward button pressed');    
  //   alert('Clicking in back or forward may result in loss of data or unexpected behavior.');
  //   event.preventDefault();

  //   return false;
  // }
}
